import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import {
  TagContainer,
  TagLabel,
  TagInfo,
  TagHoverBox,
  TagHoverBoxContent,
  PointerDiv,
  TagLabelAndInfoContainer,
} from './styledComponents';

// Must match the transparent top padding of the hover box, which bridges the hover continuity.
const HOVER_BOX_TOP_PADDING = 8;
// Gap kept between the hover box and the bottom of the viewport.
const HOVER_BOX_VIEWPORT_MARGIN = 16;
const HOVER_BOX_MAX_CONTENT_HEIGHT = 240;
const HOVER_BOX_MIN_CONTENT_HEIGHT = 64;

export const Tags = ({
  items,
  isNestedContent = false,
  onlyLabel = false,
  labelCustomStyle = {},
}) => {
  const containerRef = useRef(null);
  const [hoverBoxLayout, setHoverBoxLayout] = useState(null);

  if (_.isEmpty(items)) {
    return '-';
  }

  const sortedItems = items.sort();

  if (onlyLabel) {
    return (
      <>
        {sortedItems.map((item, index) => (
          <TagLabel className="box" customStyle={labelCustomStyle} key={index}>
            {item}
          </TagLabel>
        ))}
      </>
    );
  }

  /**
   * The hover box is position fixed so the overflow of the list cells cannot clip it: its viewport
   * coordinates are refreshed each time the hover starts, since the row may have scrolled or moved.
   * Its content height is capped to the room left below the tag so it never overflows the screen.
   */
  const handleMouseEnter = () => {
    if (!containerRef.current) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();

    const availableHeight =
      window.innerHeight - containerRect.bottom - HOVER_BOX_TOP_PADDING - HOVER_BOX_VIEWPORT_MARGIN;

    const contentMaxHeight = Math.max(
      HOVER_BOX_MIN_CONTENT_HEIGHT,
      Math.min(HOVER_BOX_MAX_CONTENT_HEIGHT, availableHeight),
    );

    setHoverBoxLayout({
      position: { top: containerRect.bottom, left: containerRect.left },
      contentMaxHeight,
    });
  };

  return (
    <TagContainer pointer={true} ref={containerRef} onMouseEnter={handleMouseEnter}>
      <TagLabelAndInfoContainer>
        <TagLabel isNestedContent={isNestedContent}>{sortedItems[0]}</TagLabel>
        {sortedItems.length > 1 && <TagInfo as="TagInfo">{`+ ${sortedItems.length - 1}`}</TagInfo>}
      </TagLabelAndInfoContainer>
      <TagHoverBox style={hoverBoxLayout?.position ?? {}}>
        <PointerDiv />
        <TagHoverBoxContent maxHeight={hoverBoxLayout?.contentMaxHeight}>
          {sortedItems.map((item, index) => (
            <TagLabel className="box" key={index} style={labelCustomStyle}>
              {item}
            </TagLabel>
          ))}
        </TagHoverBoxContent>
      </TagHoverBox>
    </TagContainer>
  );
};

Tags.propTypes = {
  items: PropTypes.array.isRequired,
  isNestedContent: PropTypes.bool,
  onlyLabel: PropTypes.bool,
  // Interpolated into the label styling: either a css block or a plain style object
  labelCustomStyle: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};
